/**
 * Thin wrapper functions over fetchWithAuth for the Student Registration
 * module's backend routes — keeps every screen from re-writing its own
 * fetch calls.
 */

import { fetchWithAuth } from '../../utils/auth';
import { API_URL } from '../../config';

const BASE = `${API_URL}/api/admin/student-registration`;

const asJson = async (res) => {
  const data = await res.json();
  return { ok: res.ok, data };
};

// Shared status derivations, used by both the check-in form and the roster
// table, so the two screens can't drift out of sync on what counts as
// "issued" vs "partial" vs "not issued".
export const bookIssuedStatus = (registration) => {
  if (!registration) return null; // no registration at all — nothing to report
  const books = registration?.books || [];
  const issuedCount = books.filter(b => b.distributed).length;
  if (books.length === 0 || issuedCount === 0) return 'not_issued'; // no catalog, or catalog exists but nothing handed out yet — both read as "not issued"
  if (issuedCount === books.length) return 'issued';
  return 'partial';
};

export const volunteerSignupStatus = (registration) =>
  registration?.volunteer_role ? 'signed_up' : 'not_signed_up';

export const BOOK_STATUS_LABELS = {
  issued: 'Books Issued',
  partial: 'Books Partially Issued',
  not_issued: 'Books Not Issued',
};

export const VOLUNTEER_STATUS_LABELS = {
  signed_up: 'Volunteering: Signed Up',
  not_signed_up: 'Not Signed Up',
};

// Maps a status key to the shared sr-status-* badge color class.
export const STATUS_BADGE_CLASS = {
  paid: 'sr-status-paid',
  issued: 'sr-status-paid',
  signed_up: 'sr-status-paid',
  partial: 'sr-status-partial',
  pending: 'sr-status-pending',
  not_issued: 'sr-status-pending',
  not_signed_up: 'sr-status-pending',
};

// Students
export const searchStudents = (value, academicYearId) =>
  fetchWithAuth(`${BASE}/students/search?value=${encodeURIComponent(value)}${academicYearId ? `&academic_year_id=${academicYearId}` : ''}`).then(asJson);

export const listStudents = (search = '') =>
  fetchWithAuth(`${BASE}/students${search ? `?search=${encodeURIComponent(search)}` : ''}`).then(asJson);

export const createStudent = (data) =>
  fetchWithAuth(`${BASE}/students`, { method: 'POST', body: JSON.stringify(data) }).then(asJson);

export const updateStudent = (id, data) =>
  fetchWithAuth(`${BASE}/students/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(asJson);

// Grades
export const listGrades = (activeOnly = false) =>
  fetchWithAuth(`${BASE}/grades${activeOnly ? '?active_only=true' : ''}`).then(asJson);

export const createGrade = (name, displayOrder) =>
  fetchWithAuth(`${BASE}/grades`, { method: 'POST', body: JSON.stringify({ name, display_order: displayOrder }) }).then(asJson);

export const updateGrade = (id, data) =>
  fetchWithAuth(`${BASE}/grades/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(asJson);

export const deactivateGrade = (id) =>
  fetchWithAuth(`${BASE}/grades/${id}/deactivate`, { method: 'PUT' }).then(asJson);

// Academic years
export const listAcademicYears = () =>
  fetchWithAuth(`${BASE}/academic-years`).then(asJson);

export const createAcademicYear = (year) =>
  fetchWithAuth(`${BASE}/academic-years`, { method: 'POST', body: JSON.stringify({ year }) }).then(asJson);

export const setCurrentAcademicYear = (id) =>
  fetchWithAuth(`${BASE}/academic-years/${id}/set-current`, { method: 'PUT' }).then(asJson);

// Year fee config
export const getYearConfig = (academicYearId, branchId) =>
  fetchWithAuth(`${BASE}/year-config?academic_year_id=${academicYearId}${branchId ? `&branch_id=${branchId}` : ''}`).then(asJson);

export const upsertYearConfig = (academicYearId, feeCents, branchId) =>
  fetchWithAuth(`${BASE}/year-config`, {
    method: 'PUT',
    body: JSON.stringify({ academic_year_id: academicYearId, registration_fee_cents: feeCents, branch_id: branchId }),
  }).then(asJson);

// Grade offerings
export const listGradeOfferings = (academicYearId, branchId) =>
  fetchWithAuth(`${BASE}/grade-offerings?academic_year_id=${academicYearId}${branchId ? `&branch_id=${branchId}` : ''}`).then(asJson);

export const createGradeOffering = (academicYearId, gradeId, books, branchId) =>
  fetchWithAuth(`${BASE}/grade-offerings`, {
    method: 'POST',
    body: JSON.stringify({ academic_year_id: academicYearId, grade_id: gradeId, books, branch_id: branchId }),
  }).then(asJson);

export const updateGradeOfferingBooks = (offeringId, books, branchId) =>
  fetchWithAuth(`${BASE}/grade-offerings/${offeringId}/books`, {
    method: 'PUT',
    body: JSON.stringify({ books, branch_id: branchId }),
  }).then(asJson);

export const deleteGradeOffering = (offeringId) =>
  fetchWithAuth(`${BASE}/grade-offerings/${offeringId}`, { method: 'DELETE' }).then(asJson);

// Registrations
export const lookupRegistration = (studentId, academicYearId) =>
  fetchWithAuth(`${BASE}/registrations/lookup?student_id=${studentId}&academic_year_id=${academicYearId}`).then(asJson);

// paymentStatus/bookStatus are optional -- existing callers that only pass
// the first three args are unaffected (backend response shape is additive:
// registrations + total_count + book_status_breakdown, all computed
// server-side from the exact same filtered set, never from whatever
// happens to already be loaded in the browser).
export const listRegistrations = (academicYearId, gradeId, branchId, paymentStatus, bookStatus) =>
  fetchWithAuth(`${BASE}/registrations?academic_year_id=${academicYearId}${gradeId ? `&grade_id=${gradeId}` : ''}${branchId ? `&branch_id=${branchId}` : ''}${paymentStatus ? `&payment_status=${paymentStatus}` : ''}${bookStatus ? `&book_status=${bookStatus}` : ''}`).then(asJson);

export const registerStudent = (payload) =>
  fetchWithAuth(`${BASE}/register-student`, { method: 'POST', body: JSON.stringify(payload) }).then(asJson);

// CSV bulk import — FormData, not JSON, so fetchWithAuth skips forcing
// Content-Type and lets the browser set its own multipart boundary.
export const importStudents = (file, branchId, academicYearId) => {
  const form = new FormData();
  form.append('file', file);
  form.append('branch_id', branchId);
  form.append('academic_year_id', academicYearId);
  return fetchWithAuth(`${BASE}/import`, { method: 'POST', body: form }).then(asJson);
};

// Backfills any registration's missing book-distribution rows from its
// grade's current offering (super_admin only) -- for registrations
// created before that grade's book list was configured (e.g. CSV import
// that ran ahead of Setup).
export const syncBooks = (academicYearId, branchId) =>
  fetchWithAuth(`${BASE}/sync-books`, {
    method: 'POST', body: JSON.stringify({ academic_year_id: academicYearId, branch_id: branchId }),
  }).then(asJson);

export const updateRegistration = (id, data) =>
  fetchWithAuth(`${BASE}/registrations/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(asJson);

// Soft withdraw/reinstate for the year -- deliberately separate from
// updateRegistration above so the Check-in page can show a clear
// confirm-before-acting button, same pattern as User Management's
// Deactivate/Reactivate. Everything else about the registration (fee,
// payment, section, books) is left untouched by this call.
export const setRegistrationActive = (id, isActive) =>
  fetchWithAuth(`${BASE}/registrations/${id}/active`, {
    method: 'PUT', body: JSON.stringify({ is_active: isActive }),
  }).then(asJson);

export const updateRegistrationBooks = (id, updates) =>
  fetchWithAuth(`${BASE}/registrations/${id}/books`, { method: 'PUT', body: JSON.stringify({ updates }) }).then(asJson);

// The Evaluator's one action — deliberately separate from updateRegistration
// above, matching the backend's own separation (grade changes are the only
// thing require_permission('grade_evaluation') can do). Also used by the
// combined admin Check-In page's grade edit, which never passes a comment --
// the backend only appends one when it's actually given, so that's fine.
//
// If the old grade had any book already marked distributed, the backend
// responds with {needs_confirmation: true, issued_books: [...]} instead of
// applying the change -- callers must show the user what's about to be
// discarded and resubmit with confirmBookRemoval=true to actually proceed.
export const updateRegistrationGrade = (id, gradeId, comment, confirmBookRemoval) =>
  fetchWithAuth(`${BASE}/registrations/${id}/grade`, {
    method: 'PUT', body: JSON.stringify({ grade_id: gradeId, comment: comment || undefined, confirm_book_removal: !!confirmBookRemoval }),
  }).then(asJson);
